/**
 * Cognito Hosted UI authentication (R24.4).
 *
 * The SPA no longer runs a hand-built SRP form. Instead it uses Cognito's
 * Hosted UI at the custom auth domain (`auth.rufus.jovial.ninja`) via the OAuth
 * **authorization-code (PKCE)** flow. Hosted UI handles everything the old
 * custom form could not: first-login password set, TOTP enrollment (QR), and
 * social federation (Google / Login with Amazon). The SPA is a thin OAuth
 * client:
 *
 *   1. {@link CognitoAuth.redirectToLogin} sends the browser to the Hosted UI
 *      authorize endpoint (optionally jumping straight to a social provider),
 *      carrying a PKCE `code_challenge` + `state`.
 *   2. Hosted UI redirects back to the SPA with `?code=…&state=…`.
 *   3. {@link CognitoAuth.handleRedirectCallback} exchanges the code for tokens
 *      at the token endpoint using the stored PKCE `code_verifier`.
 *   4. Tokens are held for the session; {@link CognitoAuth.idToken} is the API
 *      bearer, and {@link CognitoAuth.signOut} clears them + hits Hosted UI logout.
 *
 * PKCE (S256) is used even though the SPA is a public client (no secret) — the
 * standard for browser code flows. The transport (redirect + fetch) is injected
 * via {@link AuthTransport} so this module is unit-testable without a real
 * browser navigation or network.
 */

import type { RufusRuntimeConfig, SocialProvider } from "./config.js";

/** The tokens a successful sign-in yields. */
export interface AuthTokens {
  /** The Cognito id token (JWT) attached as the API bearer. */
  readonly idToken: string;
  readonly accessToken: string;
  readonly refreshToken?: string;
  /** Absolute epoch-ms the access/id token expires (for refresh timing). */
  readonly expiresAt?: number;
}

/** The Cognito Hosted UI OAuth provider ids (as recognized by `identity_provider`). */
const PROVIDER_PARAM: Record<SocialProvider, string> = {
  Google: "Google",
  Amazon: "LoginWithAmazon",
};

const STORAGE_PKCE_VERIFIER = "rufus.pkce.verifier";
const STORAGE_STATE = "rufus.oauth.state";
const STORAGE_TOKENS = "rufus.tokens";

/**
 * Conservative fallback token validity used ONLY if a token response omits
 * `expires_in` (Cognito always sends it, so this is defense-in-depth). Short on
 * purpose: a missing expiry resolves to "refresh again shortly", which is
 * bounded and self-corrects once a response carries a real `expires_in` —
 * never an every-request refresh storm. 5 minutes is long enough to avoid
 * churn, short enough to recover quickly.
 */
const FALLBACK_TOKEN_VALIDITY_MS = 5 * 60_000;

/**
 * A STRUCTURED token-endpoint error (session-hardening fix 7). Carries the HTTP
 * `status` and the parsed OAuth `error` code so a caller can distinguish a FATAL
 * refresh (the refresh token is dead — `invalid_grant` / any OAuth 4xx) from a
 * TRANSIENT one (network/5xx) by FIELD, not a brittle message string-match. A
 * network failure (fetch rejects) is NOT an AuthTokenError — it surfaces as the
 * raw thrown value, which the caller treats as transient.
 */
export class AuthTokenError extends Error {
  constructor(
    readonly status: number | undefined,
    readonly oauthError: string | undefined,
    message: string,
  ) {
    super(message);
    this.name = "AuthTokenError";
  }
  /**
   * FATAL = the session cannot be renewed (dead/invalid refresh token). An OAuth
   * `invalid_grant`, or a 4xx EXCEPT the transient 429 (rate-limited) / 408
   * (request timeout) — those are retryable and must NOT force a logout
   * (dual-review: a 429 wrongly treated as fatal logs out a healthy session).
   */
  get isFatal(): boolean {
    if (this.oauthError === "invalid_grant") return true;
    if (this.status === undefined) return false;
    if (this.status === 429 || this.status === 408) return false; // transient
    return this.status >= 400 && this.status < 500;
  }
}

/**
 * Decode a JWT's PAYLOAD (the middle segment) WITHOUT verifying the signature —
 * READ-ONLY, for reading `exp`/`iat` to time the proactive refresh only (the
 * backend authorizers do the real verification). Returns null on any malformed
 * input rather than throwing, so the token flow never breaks on a odd token.
 */
function decodeJwtPayload(jwt: string): Record<string, unknown> | null {
  try {
    const parts = jwt.split(".");
    if (parts.length !== 3) return null;
    const base64 = parts[1]!.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Derive the absolute `expiresAt` (epoch-ms) for a token response (session-
 * hardening fix 6). The ID TOKEN governs — it is what the authorizers validate —
 * so prefer its lifetime, but SKEW-SAFE: anchor the token's `(exp - iat)` LIFESPAN
 * to the LOCAL clock (`Date.now() + lifespan`), immune to a fixed server/browser
 * clock offset that an absolute `exp` would import. Clamp by the access-token
 * `expires_in` when present (never outlive it). Falls back to `expires_in`, then
 * to {@link FALLBACK_TOKEN_VALIDITY_MS}, when the id token is undecodable.
 */
function deriveExpiresAt(idToken: string, expiresIn: number | undefined): number {
  const now = Date.now();
  const accessCap = typeof expiresIn === "number" ? now + expiresIn * 1000 : undefined;
  const payload = decodeJwtPayload(idToken);
  const exp = typeof payload?.exp === "number" ? payload.exp : undefined;
  const iat = typeof payload?.iat === "number" ? payload.iat : undefined;
  if (exp !== undefined && iat !== undefined && exp > iat) {
    const idExpiresAt = now + (exp - iat) * 1000; // lifespan anchored to local now
    return accessCap !== undefined ? Math.min(idExpiresAt, accessCap) : idExpiresAt;
  }
  return accessCap ?? now + FALLBACK_TOKEN_VALIDITY_MS;
}

/**
 * The browser/network operations the flow drives, injected so the flow is
 * testable without real navigation or `fetch`. Production wires the real
 * `window` + `fetch` + `sessionStorage` in {@link createCognitoAuth}.
 */
export interface AuthTransport {
  /** Current URL (to read `?code=`/`state`), e.g. `window.location.href`. */
  currentUrl(): string;
  /** Navigate the browser to `url` (Hosted UI authorize/logout). */
  redirect(url: string): void;
  /** Replace the current URL without navigation (to strip `?code=` after exchange). */
  replaceUrl(url: string): void;
  fetch: typeof fetch;
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  /** Cryptographically-random bytes for PKCE (real: `crypto.getRandomValues`). */
  randomBytes(length: number): Uint8Array;
  /** SHA-256 of `input`, returned as bytes (real: `crypto.subtle.digest`). */
  sha256(input: string): Promise<Uint8Array>;
}

/** base64url (no padding) — the encoding PKCE + OAuth state use. */
function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * The auth facade the app uses. Holds the signed-in tokens so callers read
 * `idToken` for the API bearer, and drives the Hosted UI redirect + code
 * exchange.
 */
/** Notified whenever the held tokens change (refresh, sign-in, sign-out). */
export type AuthChangeListener = () => void;

export class CognitoAuth {
  private tokens: AuthTokens | null;
  private readonly changeListeners = new Set<AuthChangeListener>();
  /** The in-flight refresh promise, or null — the single-flight guard (see refresh). */
  private refreshInFlight: Promise<boolean> | null = null;
  /**
   * Monotonic SESSION-IDENTITY counter (session-hardening fix 1). Bumped on every
   * session-identity change — a sign-out, and the start of a new login's code
   * exchange. A `doRefresh` captures it before its network `await` and DROPS its
   * result (no token write, no clear, no notify) if the epoch changed meanwhile —
   * so a refresh that was in flight when the user signed out or logged in can
   * never resurrect the old session nor clobber the new one (the zombie-token race
   * behind the "403 after logout+login" symptom), in EITHER the success or the
   * catch path.
   */
  private sessionEpoch = 0;

  constructor(
    private readonly config: RufusRuntimeConfig,
    private readonly transport: AuthTransport,
    private readonly redirectUri: string,
  ) {
    this.tokens = this.readStoredTokens();
  }

  /** The id-token bearer to attach to API calls, or null when signed out. */
  get idToken(): string | null {
    return this.tokens?.idToken ?? null;
  }

  isAuthenticated(): boolean {
    return this.tokens !== null;
  }

  /**
   * Subscribe to token changes. The listener fires after ANY change to the held
   * tokens — a successful {@link refresh}, the initial code exchange, or a
   * sign-out — so React consumers can re-read `idToken` and re-drive effects
   * that captured the old value (notably the WebSocket, which is opened with the
   * token as a query param and must reconnect with a fresh one). Returns an
   * unsubscribe function.
   */
  onChange(listener: AuthChangeListener): () => void {
    this.changeListeners.add(listener);
    return () => this.changeListeners.delete(listener);
  }

  private notifyChange(): void {
    for (const l of this.changeListeners) l();
  }

  /**
   * Send the browser to the Hosted UI authorize endpoint. Pass a `provider` to
   * jump straight to Google/Amazon; omit it to show the Hosted UI chooser
   * (native email/password + any wired social buttons). Generates + stores the
   * PKCE verifier and an anti-CSRF `state` first.
   */
  async redirectToLogin(provider?: SocialProvider): Promise<void> {
    const verifier = base64UrlEncode(this.transport.randomBytes(32));
    const state = base64UrlEncode(this.transport.randomBytes(16));
    const challenge = base64UrlEncode(await this.transport.sha256(verifier));

    this.transport.setItem(STORAGE_PKCE_VERIFIER, verifier);
    this.transport.setItem(STORAGE_STATE, state);

    const params = new URLSearchParams({
      response_type: "code",
      client_id: this.config.userPoolClientId,
      redirect_uri: this.redirectUri,
      scope: "openid email profile",
      state,
      code_challenge: challenge,
      code_challenge_method: "S256",
    });
    if (provider) params.set("identity_provider", PROVIDER_PARAM[provider]);

    this.transport.redirect(
      `${this.authBase()}/oauth2/authorize?${params.toString()}`,
    );
  }

  /**
   * If the current URL carries `?code=…` from a Hosted UI redirect, validate
   * `state`, exchange the code for tokens, store them, and strip the query
   * string. Returns true when a code was present and exchanged, false when there
   * was nothing to do (normal load). Throws on a state mismatch or token error.
   */
  async handleRedirectCallback(): Promise<boolean> {
    const url = new URL(this.transport.currentUrl());
    const code = url.searchParams.get("code");
    if (!code) return false;

    // A new login is a new session identity: bump the epoch at the START of the
    // exchange (before the POST) so a refresh that was in flight from a PRIOR
    // session has its result dropped by the epoch guard rather than clobbering the
    // tokens this exchange is about to establish. Also orphan any in-flight refresh
    // promise so a concurrent refresh() does not join the prior session's doomed
    // run (mirrors signOut; dual-review finding).
    this.sessionEpoch += 1;
    this.refreshInFlight = null;
    const exchangeEpoch = this.sessionEpoch;

    const returnedState = url.searchParams.get("state");
    const expectedState = this.transport.getItem(STORAGE_STATE);
    if (!expectedState || returnedState !== expectedState) {
      this.clearTransient();
      throw new Error("OAuth state mismatch — possible CSRF; sign-in aborted.");
    }

    const verifier = this.transport.getItem(STORAGE_PKCE_VERIFIER);
    if (!verifier) {
      this.clearTransient();
      throw new Error("Missing PKCE verifier — restart sign-in.");
    }

    const body = new URLSearchParams({
      grant_type: "authorization_code",
      client_id: this.config.userPoolClientId,
      code,
      redirect_uri: this.redirectUri,
      code_verifier: verifier,
    });
    const tokens = await this.postToken(body);
    // EPOCH GUARD (dual-review — the handleRedirectCallback twin of the doRefresh
    // guard): if a sign-out (or another login) superseded this exchange while the
    // POST was in flight, DROP the result rather than resurrecting/clobbering —
    // the exchanged tokens belong to a session that is no longer current.
    if (this.sessionEpoch !== exchangeEpoch) {
      this.clearTransient();
      return false;
    }
    this.tokens = tokens;
    this.writeStoredTokens(tokens);
    this.clearTransient();
    this.notifyChange();

    // Strip ?code/&state so a reload doesn't re-exchange a spent code.
    url.searchParams.delete("code");
    url.searchParams.delete("state");
    this.transport.replaceUrl(url.toString());
    return true;
  }

  /**
   * Refresh the tokens using the stored refresh token. Returns true on success,
   * false when there is no refresh token to use. On a refresh failure the tokens
   * are cleared (the caller should redirect to login).
   *
   * SINGLE-FLIGHT: when a token expires, the REST path (pre-expiry check and/or
   * a reactive 401) and the WebSocket path (rejected handshake) can all ask to
   * refresh at nearly the same moment. Without a guard that fires several
   * concurrent `grant_type=refresh_token` POSTs for one logical refresh — wasteful
   * and racy (a later response could clobber an earlier one). So concurrent
   * callers share ONE in-flight refresh promise; the guard clears when it settles.
   */
  async refresh(): Promise<boolean> {
    if (this.refreshInFlight) return this.refreshInFlight;
    const run = this.doRefresh();
    this.refreshInFlight = run;
    try {
      return await run;
    } finally {
      // Clear only if THIS run is still the current one (defensive against a
      // settled promise being replaced — it won't be here, but keeps the guard
      // honest if the method is ever re-entered).
      if (this.refreshInFlight === run) this.refreshInFlight = null;
    }
  }

  /** The actual refresh-token grant. Wrapped by {@link refresh} for single-flight. */
  private async doRefresh(): Promise<boolean> {
    // Capture the session identity BEFORE any await. If it changes (sign-out, or a
    // new login's code exchange) while this refresh is in flight, the result is
    // DROPPED — it belongs to a session that no longer exists (fix 1).
    const epoch = this.sessionEpoch;
    const refreshToken = this.tokens?.refreshToken;
    if (!refreshToken) {
      // No refresh token to use → this session cannot renew. Clear to a clean
      // SIGNED-OUT state so the app never sits half-authenticated holding a dead id
      // token — the zombie-token state behind the "logged out, back in, still 403"
      // symptom. Guarded so we never emit a spurious change event when already
      // signed out. (This branch does no await, so no epoch re-check is needed.)
      if (this.tokens !== null) {
        this.tokens = null;
        this.transport.removeItem(STORAGE_TOKENS);
        this.notifyChange();
      }
      return false;
    }
    try {
      const body = new URLSearchParams({
        grant_type: "refresh_token",
        client_id: this.config.userPoolClientId,
        refresh_token: refreshToken,
      });
      const refreshed = await this.postToken(body);
      // EPOCH GUARD (fix 1, success path): a sign-out or a new login happened while
      // the POST was in flight → DROP this result. Writing it would resurrect the
      // old session (after sign-out) or clobber the new one (after login). Touch
      // nothing, do not notify.
      if (this.sessionEpoch !== epoch) return false;
      // Keep the existing refresh token when the response omits one (the common
      // case), but PREFER a rotated one when Cognito returns a fresh `refresh_token`
      // (refresh-token rotation) — else the NEXT refresh would use a dead token.
      const merged: AuthTokens = { ...refreshed, refreshToken: refreshed.refreshToken ?? refreshToken };
      this.tokens = merged;
      this.writeStoredTokens(merged);
      this.notifyChange();
      return true;
    } catch (err) {
      // EPOCH GUARD (fix 1, CATCH path — the critical design-review gap): if the
      // session changed under us, do NOT clear/notify — that would nuke the NEW
      // session a login just established. The superseded refresh simply fails.
      if (this.sessionEpoch !== epoch) return false;
      // TRANSIENT vs FATAL (fix 7): only a FATAL refresh (the refresh token is dead
      // — invalid_grant / an OAuth 4xx) signs the user out. A TRANSIENT failure
      // (network reject / 5xx) KEEPS the tokens so a later attempt retries — a
      // momentary failure to reach the endpoint is not evidence the session died.
      const fatal = err instanceof AuthTokenError && err.isFatal;
      if (fatal) {
        this.tokens = null;
        this.transport.removeItem(STORAGE_TOKENS);
        this.notifyChange();
      }
      return false;
    }
  }

  /**
   * True when the current id token is absent OR within `withinMs` of its expiry
   * (or has no known expiry — treated as "refresh to be safe"). Reads the
   * `expiresAt` the token exchange stored; the REST client calls this before a
   * request to refresh PROACTIVELY, so the server never sees an expired token
   * and the player never eats the failed-401 round-trip. `now` is injectable for
   * deterministic tests. Returns false when signed out (nothing to refresh).
   */
  isExpiringWithin(withinMs: number, now: number = Date.now()): boolean {
    if (!this.tokens) return false;
    const expiresAt = this.tokens.expiresAt;
    // No known expiry → conservatively treat as expiring so we refresh.
    if (typeof expiresAt !== "number") return true;
    return now >= expiresAt - withinMs;
  }

  /**
   * Sign out. There are TWO independent session layers, and both must be
   * cleared: (1) our stored tokens in `sessionStorage`, cleared here directly,
   * and (2) the Hosted UI's OWN session cookie on the auth domain — clearing
   * sessionStorage does NOT end that, so we redirect to the Hosted UI `/logout`
   * endpoint, which clears the Cognito session cookie and returns to the SPA.
   * Skipping the redirect would leave the Hosted UI silently re-authenticating
   * the user on the next `redirectToLogin`.
   */
  signOut(): void {
    // New session identity: supersede any in-flight refresh (its result is dropped
    // by the epoch guard in doRefresh) and orphan the shared promise so a later
    // refresh() does not join it (fix 1).
    this.sessionEpoch += 1;
    this.refreshInFlight = null;
    this.tokens = null;
    this.transport.removeItem(STORAGE_TOKENS);
    this.clearTransient();
    this.notifyChange();
    const params = new URLSearchParams({
      client_id: this.config.userPoolClientId,
      logout_uri: this.redirectUri,
    });
    this.transport.redirect(`${this.authBase()}/logout?${params.toString()}`);
  }

  // -- internals ------------------------------------------------------------

  private authBase(): string {
    // authDomain may arrive with or without a scheme; normalize to https origin.
    const raw = this.config.authDomain;
    const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    return withScheme.replace(/\/+$/, "");
  }

  private async postToken(body: URLSearchParams): Promise<AuthTokens> {
    const res = await this.transport.fetch(`${this.authBase()}/oauth2/token`, {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });
    if (!res.ok) {
      // Surface the OAuth error body when present (`{error, error_description}`)
      // so a Hosted UI / client misconfig is diagnosable, not just an opaque
      // status. Fall back to the status if the body isn't the expected shape.
      let detail = `HTTP ${res.status}`;
      let oauthError: string | undefined;
      try {
        const errBody = (await res.json()) as {
          error?: string;
          error_description?: string;
        };
        if (errBody?.error) {
          oauthError = errBody.error;
          detail = errBody.error_description
            ? `${errBody.error}: ${errBody.error_description}`
            : errBody.error;
        }
      } catch {
        // non-JSON error body; keep the status detail.
      }
      // STRUCTURED error (fix 7): carry status + OAuth error so a caller keys on
      // fields, not prose, to tell a FATAL (dead refresh token) from a TRANSIENT
      // failure.
      throw new AuthTokenError(res.status, oauthError, `Token endpoint error (${detail}).`);
    }
    const json = (await res.json()) as {
      id_token?: string;
      access_token?: string;
      refresh_token?: string;
      expires_in?: number;
    };
    if (!json.id_token || !json.access_token) {
      throw new Error("Token response is missing id/access token.");
    }
    return {
      idToken: json.id_token,
      accessToken: json.access_token,
      refreshToken: json.refresh_token,
      // Expiry derived from the ID TOKEN's lifetime (what the authorizers
      // validate), skew-safe (lifespan anchored to the local clock), clamped by
      // the access-token `expires_in`; falls back to `expires_in` then to a short
      // conservative validity when the id token is undecodable (fix 6). ALWAYS
      // recorded — an undefined expiresAt would make isExpiringWithin refresh on
      // every request.
      expiresAt: deriveExpiresAt(json.id_token, json.expires_in),
    };
  }

  private readStoredTokens(): AuthTokens | null {
    const raw = this.transport.getItem(STORAGE_TOKENS);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as AuthTokens;
      if (!parsed.idToken || !parsed.accessToken) return null;
      // EXPIRED-ON-LOAD (fix 2): an id token already past its expiry must NOT be
      // trusted as valid (the zombie that 403s). But a session with a refresh
      // token is RECOVERABLE — keep it so the bootstrap proactive refresh (see
      // main.tsx) can renew it silently, rather than forcing a needless re-login
      // every id-token lifetime. Only a dead token with NO refresh token is
      // genuinely signed-out.
      const expired =
        typeof parsed.expiresAt === "number" && Date.now() >= parsed.expiresAt;
      if (expired && !parsed.refreshToken) return null;
      return parsed;
    } catch {
      return null;
    }
  }

  private writeStoredTokens(tokens: AuthTokens): void {
    this.transport.setItem(STORAGE_TOKENS, JSON.stringify(tokens));
  }

  private clearTransient(): void {
    this.transport.removeItem(STORAGE_PKCE_VERIFIER);
    this.transport.removeItem(STORAGE_STATE);
  }
}

/**
 * Build a browser {@link AuthTransport} over `window`, `fetch`, `sessionStorage`
 * and Web Crypto. Refresh tokens live in `sessionStorage` (a documented
 * tradeoff — acceptable for an invite-only app; revisit for Phase 4 public
 * signup).
 */
function browserTransport(): AuthTransport {
  return {
    currentUrl: () => window.location.href,
    redirect: (url) => window.location.assign(url),
    replaceUrl: (url) => window.history.replaceState(null, "", url),
    fetch: (...args) => globalThis.fetch(...args),
    getItem: (k) => window.sessionStorage.getItem(k),
    setItem: (k, v) => window.sessionStorage.setItem(k, v),
    removeItem: (k) => window.sessionStorage.removeItem(k),
    randomBytes: (length) =>
      crypto.getRandomValues(new Uint8Array(length)),
    sha256: async (input) => {
      const digest = await crypto.subtle.digest(
        "SHA-256",
        new TextEncoder().encode(input),
      );
      return new Uint8Array(digest);
    },
  };
}

/**
 * Build a {@link CognitoAuth} for the running SPA. The redirect URI defaults to
 * the current origin (works for both the production site domain and localhost
 * dev — both are registered as Hosted UI callback URLs by the ComputeStack), so
 * no separate config field is needed. Tests construct {@link CognitoAuth}
 * directly with a fake transport.
 */
export function createCognitoAuth(
  config: RufusRuntimeConfig,
  transport: AuthTransport = browserTransport(),
  redirectUri: string = window.location.origin,
): CognitoAuth {
  return new CognitoAuth(config, transport, redirectUri);
}
